import React, { useEffect, useMemo, useState } from "react";
import NumbersGrid from "./NumbersGrid";
import ActivityChart from "./ActivityChart";
import StudentProgressTable from "./StudentProgressTable";
import StatCard from "./StatCard";
import { getNumbers, getStudentActivity, getTrainerActivity } from "../pages/services/statsService";
import "../styles/Dashboard.css";

const RANGE_PRESETS = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
];

const istDateString = (date) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(date);

const rangeFromDays = (days) => {
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
  return { from: istDateString(from), to: istDateString(to) };
};

const formatDurationSeconds = (seconds) => {
  if (seconds === null || seconds === undefined) return "—";
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.round((seconds % 3600) / 60);
  if (hrs === 0) return `${mins}m`;
  return `${hrs}h ${mins}m`;
};

const Dashboard = () => {
  const [rangeDays, setRangeDays] = useState(30);
  const range = useMemo(() => rangeFromDays(rangeDays), [rangeDays]);

  const [numbers, setNumbers] = useState(null);
  const [numbersLoading, setNumbersLoading] = useState(true);
  const [numbersError, setNumbersError] = useState(null);

  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityError, setActivityError] = useState(null);

  const [trainerActivity, setTrainerActivity] = useState(null);
  const [trainerLoading, setTrainerLoading] = useState(true);
  const [trainerError, setTrainerError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setNumbersLoading(true);
    getNumbers()
      .then((data) => !cancelled && setNumbers(data))
      .catch((err) => !cancelled && setNumbersError(err.message))
      .finally(() => !cancelled && setNumbersLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setActivityLoading(true);
    setActivityError(null);
    getStudentActivity(range.from, range.to)
      .then((data) => !cancelled && setActivity(data))
      .catch((err) => !cancelled && setActivityError(err.message))
      .finally(() => !cancelled && setActivityLoading(false));
    return () => {
      cancelled = true;
    };
  }, [range.from, range.to]);

  useEffect(() => {
    let cancelled = false;
    setTrainerLoading(true);
    setTrainerError(null);
    getTrainerActivity(range.from, range.to)
      .then((data) => !cancelled && setTrainerActivity(data))
      .catch((err) => !cancelled && setTrainerError(err.message))
      .finally(() => !cancelled && setTrainerLoading(false));
    return () => {
      cancelled = true;
    };
  }, [range.from, range.to]);

  return (
    <div className="lp-page dash-page">
      <div className="lp-header">
        <div className="lp-header-left">
          <h1 className="lp-title">Dashboard</h1>
        </div>
        <div className="dash-range-filter">
          {RANGE_PRESETS.map((preset) => (
            <button
              key={preset.days}
              type="button"
              className={`dash-range-btn ${rangeDays === preset.days ? "dash-range-btn-active" : ""}`}
              onClick={() => setRangeDays(preset.days)}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      <NumbersGrid numbers={numbers} loading={numbersLoading} error={numbersError} />

      <section className="dash-section">
        <h2 className="dash-section-title">Student activity</h2>
        <div className="dash-card">
          <ActivityChart data={activity} loading={activityLoading} error={activityError} />
        </div>
      </section>

      <StudentProgressTable />

      <section className="dash-section">
        <h2 className="dash-section-title">Trainer activity</h2>
        {trainerError ? (
          <div className="dash-section-error">Couldn't load trainer activity: {trainerError}</div>
        ) : (
          <div className="dash-numbers-grid dash-numbers-grid-narrow">
            <StatCard
              label="App access time"
              displayValue={trainerLoading ? undefined : formatDurationSeconds(trainerActivity?.appAccessTimeSeconds)}
              loading={trainerLoading}
            />
            <StatCard
              label="Notes completion"
              value={trainerActivity?.notesCompletionCount}
              loading={trainerLoading}
            />
          </div>
        )}
      </section>
    </div>
  );
};

export default Dashboard;
